import { useEffect, useState } from "react";
import {
  Modal,
  Form,
  Input,
  InputNumber,
  Switch,
  Space,
  Button,
  Typography,
  Tabs,
  Select,
  message,
  Slider,
  Row,
  Col,
} from "antd";
import type { TabsProps } from "antd";
import {
  getDeviceSettings,
  setDeviceSettingsBulk,
  pullFromServer,
 } from "../services/deviceSettings";
import { invoke } from "@tauri-apps/api/core";
import { isTauri } from "../utils/tauri";
import { refreshRuntimeConfig } from "../services/config";
import { printDemoOrderTicketStyled } from "../pos/print";
type Props = { open: boolean; onClose: () => void; canEdit: boolean };

export default function DeviceSettingsModal({ open, onClose, canEdit }: Props) {
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  const KEYS = [
    // API/app
    "api_base_url",
    "admin_panel_url",
    "local_order_seq",
    "auto_sync_sec",
    "lock_duration_sec",
    // Štampa
    "printer_ip",
    "printer_share",
    "print_after_fiscal",
    // Fiskal
    "fiscal_ip",
    "fiscal_port",
    // Kuhinja
    "kitchen_max_cols",
    "kitchen_card_width",
    "kitchen_sound_enabled",
    "kitchen_sound_volume",
    "kitchen_sound_kind",
    // Etikete
    "label_kitchen_title",
    "label_table",
    "label_take",
    "label_ready",
    //Tedt
    "disable_order_button",
    "monry_enabled",
    "monry_ws_host",
    "monry_ws_port",
    "monry_terminal_id",
    "monry_merchant_id",
    "monry_currency",
    "monry_timeout_sec",
    //KIOSK DESIGN
     "kiosk_grid_cols",
    "kiosk_item_img_h",
    "kiosk_cat_img_s",
    "kiosk_left_ratio",
    "kiosk_color_text",
    "kiosk_color_bg",
    "kiosk_color_button",
  ];
  //eslint-disable-next-line
  useEffect(() => {
    if (open) load();
    //eslint-disable-next-line
  }, [open]);

  async function load() {
    setLoading(true);
    try {
      try {
        await pullFromServer(KEYS);
      } catch {}
      const s = await getDeviceSettings(KEYS);
      form.setFieldsValue({
        // API
        api_base_url: s["api_base_url"] || "https://new.itable.app/api",
        admin_panel_url: s["admin_panel_url"] ?? "https://portal.itable.app",
        local_order_seq: Number(s["local_order_seq"] ?? 1),
        auto_sync_sec: Number(s["auto_sync_sec"] ?? 10),
        lock_duration_sec:
          Number(s["lock_duration_sec"] ?? 30000) / 1000 > 10800
            ? 10800
            : Number(s["lock_duration_sec"] ?? 30000) / 1000,
        monry_enabled: String(s["monry_enabled"] ?? "1") === "1",
        monry_ws_host: s["monry_ws_host"] || "127.0.0.1",
        monry_ws_port: Number(s["monry_ws_port"] ?? 9000),
        monry_terminal_id: s["monry_terminal_id"] || "",
        monry_merchant_id: s["monry_merchant_id"] || "",
        monry_currency: s["monry_currency"] || "BAM",
        monry_timeout_sec: Number(s["monry_timeout_sec"] ?? 120),
        // Štampa
        printer_ip: s["printer_ip"] || "",
        printer_share: s["printer_share"] || "",
        print_after_fiscal: !!s["print_after_fiscal"],

        // Fiskal
        fiscal_ip: s["fiscal_ip"] || "192.168.88.29",
        fiscal_port: Number(s["fiscal_port"] ?? 8085),
        disable_order_button: !!s["disable_order_button"],

        // Kuhinja
        kitchen_max_cols: clampInt(s["kitchen_max_cols"], 4, 1, 8),
        kitchen_card_width: clampInt(s["kitchen_card_width"], 320, 260, 520),
        kitchen_sound_enabled: !!s["kitchen_sound_enabled"],
        kitchen_sound_volume: clampInt(s["kitchen_sound_volume"], 80, 0, 100),
        kitchen_sound_kind: s["kitchen_sound_kind"] || "chime",

        // Etikete
        label_kitchen_title:
          s["label_kitchen_title"] || "Kuhinja — narudžbe za pripremu",
        label_table: s["label_table"] || "Sto",
        label_take: s["label_take"] || "Preuzmi",
        label_ready: s["label_ready"] || "Spremno",

           // Kiosk dizajn (NOVO)
        kiosk_grid_cols: Number(s["kiosk_grid_cols"] ?? 3),
        kiosk_item_img_h: Number(s["kiosk_item_img_h"] ?? 160),
        kiosk_cat_img_s: Number(s["kiosk_cat_img_s"] ?? 64),
        kiosk_left_ratio: Number(s["kiosk_left_ratio"] ?? 28),
        kiosk_color_text: s["kiosk_color_text"] || "#ffffff",
        kiosk_color_bg: s["kiosk_color_bg"] || "#0b0f1a",
        kiosk_color_button: s["kiosk_color_button"] || "#ffd60a",

        //Application
        kiosk_device_uuid: s["kiosk.device_uuid"] || getDeviceCode(),
        device_prefix: s["device_prefix"] || "A",
        /*    const s = await getDeviceSettings(["eft_ws_url", "eft_currency"]);
    this.url = String(s["eft_ws_url"] || "ws://127.0.0.1:8177/ecr");
    this.currency = String(s["eft_currency"] || "BAM");
        */
        eft_ws_url: s["eft_ws_url"] || "ws://127.0.0.1:8177/ecr",
        eft_currency: s["eft_currency"] || "BAM",
      });
    } catch (e: any) {
      message.error(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }

  async function onSave() {
    const v = await form.validateFields();
    const toSave = {
      admin_panel_url: (
        v.admin_panel_url ?? "https://portal.itable.app"
      ).replace(/\/+$/g, ""),
      api_base_url: (v.api_base_url || "").replace(/\/+$/g, ""),
      local_order_seq: Number(v.local_order_seq || 1),
      auto_sync_sec: Number(v.auto_sync_sec || 10),
      lock_duration_sec: Number(v.lock_duration_sec || 30) * 1000,

      printer_ip: v.printer_ip || "",
      printer_share: v.printer_share || "",
      print_after_fiscal: !!v.print_after_fiscal,

      fiscal_ip: v.fiscal_ip || "",
      fiscal_port: Number(v.fiscal_port || 9100),

      kitchen_max_cols: clampInt(v.kitchen_max_cols, 4, 1, 8),
      kitchen_card_width: clampInt(v.kitchen_card_width, 320, 260, 520),
      kitchen_sound_enabled: !!v.kitchen_sound_enabled,
      kitchen_sound_volume: clampInt(v.kitchen_sound_volume, 80, 0, 100),
      kitchen_sound_kind: v.kitchen_sound_kind || "chime",
      disable_order_button: !!v.disable_order_button,
      device_prefix: v.device_prefix || "A",

      eft_ws_url: (v.eft_ws_url || "ws://127.0.0.1:8177/ecr").replace(
        /\/+$/g,
        ""
      ),
      eft_currency: (v.eft_currency || "BAM").replace(/\/+$/g, ""),
      kiosk_device_uuid: v.kiosk?.device_uuid || "",

      label_kitchen_title:
        v.label_kitchen_title || "Kuhinja — narudžbe za pripremu",
      label_table: v.label_table || "Sto",
      label_take: v.label_take || "Preuzmi",
      label_ready: v.label_ready || "Spremno",
      monry_enabled: v.monry_enabled ? 1 : 0,
      monry_ws_host: (v.monry_ws_host || "127.0.0.1").trim(),
      monry_ws_port: Number(v.monry_ws_port || 9000),
      monry_terminal_id: v.monry_terminal_id || "",
      monry_merchant_id: v.monry_merchant_id || "",
      monry_currency: v.monry_currency || "BAM",
      monry_timeout_sec: Number(v.monry_timeout_sec || 120),

        // Kiosk dizajn (NOVO)
      kiosk_grid_cols: Math.max(1, Math.min(6, Number(v.kiosk_grid_cols || 3))),
      kiosk_item_img_h: Math.max(120, Math.min(280, Number(v.kiosk_item_img_h || 160))),
      kiosk_cat_img_s: Math.max(48, Math.min(120, Number(v.kiosk_cat_img_s || 64))),
      kiosk_left_ratio: Math.max(15, Math.min(45, Number(v.kiosk_left_ratio || 28))), // %
      kiosk_color_text: v.kiosk_color_text || "#ffffff",
      kiosk_color_bg: v.kiosk_color_bg || "#0b0f1a",
      kiosk_color_button: v.kiosk_color_button || "#ffd60a",
    };

    setLoading(true);
    try {
      await setDeviceSettingsBulk(toSave);
   /*    try {
        await pushToServer(
          Object.entries(toSave).map(([key, value]) => ({ key, value }))
);
      } catch {} */
      await refreshRuntimeConfig();
      message.success("Podešavanja sačuvana.");
      onClose?.();
    } catch (e: any) {
      message.error(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }

  async function testSound() {
    const { kitchen_sound_enabled, kitchen_sound_volume, kitchen_sound_kind } =
      form.getFieldsValue();
    if (!kitchen_sound_enabled) {
      message.info("Uključi 'Zvuk za nove narudžbe'");
      return;
    }

    const volume = Number(kitchen_sound_volume || 80) / 100;
    let played = false;

    if (isTauri()) {
      try {
        await invoke("play_kitchen_sound", {
          kind: kitchen_sound_kind,
          volume,
        });
        played = true;
      } catch {}
    }

    if (!played) {
      try {
        const Ctx: any =
          (window as any).AudioContext || (window as any).webkitAudioContext;
        const ctx = new Ctx();
        await ctx.resume(); // ensure started
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.type = "sine";
        o.frequency.setValueAtTime(900, ctx.currentTime);
        g.gain.value = volume;
        o.connect(g).connect(ctx.destination);
        o.start();
        setTimeout(() => {
          o.frequency.setValueAtTime(1200, ctx.currentTime);
        }, 220);
        setTimeout(() => {
          o.stop();
          ctx.close();
        }, 480);
      } catch {}
    }
  }
  const getDeviceCode = () => {
    if (isTauri()) {
      try {
        const code = invoke<string>("get_machine_code");
        return code;
      } catch {
        return "TAURI_ERR";
      }
    } else {
      // fallback za web/React dev
      let code = localStorage.getItem("tv.uuid_device_code");
       if (!code) {
        code = "WEB" + Math.random().toString(36).substring(2, 8).toUpperCase();
        localStorage.setItem("tv.uuid_device_code", code);
      }
    

      return code;
    }
  };
  const readDevicePrintSettings = async () => {
    try {
      const s = await getDeviceSettings([
        "printer_ip",
        "printer_share",
        "print_after_fiscal",
        "print_order_on_send",
      ]);
      return {
        printerIp: (s["printer_ip"] || "").trim() || undefined,
        printerShare: (s["printer_share"] || "").trim() || undefined,
        shouldPrintAfterFiscal: String(s["print_after_fiscal"] ?? "1") === "1",
        shouldPrintOrderOnSend: String(s["print_order_on_send"] ?? "1") === "1",
      };
    } catch {
      // fallback for dev/browser
      const paf = localStorage.getItem("print_after_fiscal");
      const ip = localStorage.getItem("printer_ip") || "";
      const share = localStorage.getItem("printer_share") || "";
      const pos = localStorage.getItem("print_order_on_send") ?? "1";
      return {
        printerIp: ip || undefined,
        printerShare: share || undefined,
        shouldPrintAfterFiscal: String(paf ?? "1") === "1",
        shouldPrintOrderOnSend: String(pos ?? "1") === "1",
      };
    }
  };
  const items: TabsProps["items"] = [
    {
      key: "fiscal",
      label: "Fiskal",
      children: (
        <Space direction="vertical" style={{ width: "100%" }}>
          <Form.Item
            name="fiscal_ip"
            label="IP fiskalne kase"
            rules={[{ required: true }]}
          >
            <Input
              defaultValue={"192.168.88.46"}
              placeholder="npr. 192.168.88.46"
            />
          </Form.Item>
          <Form.Item
            name="fiscal_port"
            label="Port"
            rules={[{ required: true }]}
          >
            <InputNumber
              defaultValue={8085}
              min={1}
              max={65535}
              style={{ width: "100%" }}
            />
          </Form.Item>
          <Form.Item
            label='Isključi dugme "Narudžba"'
            name="disable_order_button"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Space>
      ),
    },
    {
      key: "print",
      label: "Štampa",
      children: (
        <Space direction="vertical" style={{ width: "100%" }}>
          <Form.Item name="printer_ip" label="Printer IP">
            <Input placeholder="npr. 192.168.1.50 (RAW 9100)" />
          </Form.Item>
          <Form.Item name="printer_share" label="Windows share">
            <Input placeholder="npr. \\\\PC\\PRINTER" />
          </Form.Item>
          <Form.Item
            name="print_after_fiscal"
            label="Štampaj nakon fiskala"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
          <Button
            onClick={async () => {
              try {
                const { printerIp, printerShare } =
                  await readDevicePrintSettings();
                await printDemoOrderTicketStyled({ printerIp, printerShare });
                message.success("DEMO order poslan na štampu.");
              } catch (e: any) {
                message.error(
                  "DEMO print nije uspio: " + String(e?.message || e)
                );
              }
            }}
          >
            DEMO order
          </Button>
          <Button
            onClick={async () => {
              try {
                const { printer_ip } = form.getFieldsValue();
                if (!printer_ip) {
                  message.warning("Unesi Printer IP.");
                  return;
                }
                const res = await invoke<string>("probe_printer_9100", {
                  ip: String(printer_ip).trim(),
                });
                message.success(res);
              } catch (e: any) {
                message.error("9100 test: " + (e?.message || e));
              }
            }}
          >
            Test 9100 konekcija
          </Button>
        </Space>
      ),
    },
    {
      key: "app",
      label: "Aplikacija",
      children: (
        <Space direction="vertical" style={{ width: "100%" }}>
          <Form.Item
            name="admin_panel_url"
            label="Admin Panel URL"
            //defaultValue="https://portal.itable.app"

            rules={[{ required: true }]}
          >
            <Input
              defaultValue={"https://portal.itable.app"}
              placeholder="https://portal.itable.app"
            />
          </Form.Item>
          <Form.Item
            name="api_base_url"
            label="API base URL"
            rules={[{ required: true }]}
          >
            <Input
              defaultValue={"https://new.itable.app/api"}
              placeholder="https://new.itable.app/api"
            />
          </Form.Item>
          {
            //Kiosk variables
            <Form.Item
              name="kiosk.device_uuid"
              label="Kiosk Device UUID"
              rules={[{ required: true }]}
              initialValue={getDeviceCode()}
            >
              <Input placeholder="Unesi Kiosk Device UUID" />
            </Form.Item>
          }
          <Form.Item
            name="eft_ws_url"
            label="EFT Terminal WS URL"
            rules={[{ required: true }]}
            initialValue={"ws://127.0.0.1:8177/ecr"}
          >
            <Input placeholder="Unesi EFT Terminal WS URL" />
          </Form.Item>

          <Form.Item
            name="eft_currency"
            label="EFT Terminal Currency"
            rules={[{ required: true }]}
            initialValue={"BAM"}
          >
            <Input placeholder="Unesi EFT Terminal Currency" />
          </Form.Item>
            <Form.Item
            name="device_prefix"
            label="Prefiks uređaja"
            rules={[{ required: true }]}
          >
            <Select
              options={[
                ...Array.from({ length: 10 }, (_, i) => ({
                  label: String(i),
                  value: String(i),
                })),
                ...Array.from({ length: 26 }, (_, i) => ({
                  label: String.fromCharCode(65 + i),
                  value: String.fromCharCode(65 + i),
                })),
              ]}
            />
          </Form.Item>

          <Form.Item
            name="local_order_seq"
            label="Lokalni brojač narudžbi"
            rules={[{ required: true }]}
          >
            <InputNumber min={1} style={{ width: "100%" }} />
          </Form.Item>
          <Row>
            <Col span={24} offset={0}>
              <Form.Item
                name="auto_sync_sec"
                label={`Auto sinhronizacija (sekunde: ${form.getFieldValue(
                  "auto_sync_sec"
                )})`}
              >
                <Slider
                  min={10}
                  max={120}
                  step={30}
                  onChange={(v) => {
                    form.setFieldValue("auto_sync_sec", v);
                  }}
                  value={form.getFieldValue("auto_sync_sec")}
                  marks={{
                    10: "10s",

                    30: "30s",
                    60: "1m",
                    120: "2m",
                  }}
                />
              </Form.Item>
            </Col>

            <Col span={24} offset={0}>
              <Form.Item
                name="lock_duration_sec"
                label={`Zaključaj nakon neaktivnosti (sekunde: ${form.getFieldValue(
                  "lock_duration_sec"
                )})`}
              >
                <Slider
                  min={10}
                  max={10800}
                  step={60}
                  onChange={(v) => {
                    form.setFieldValue("lock_duration_sec", v);
                  }}
                  value={form.getFieldValue("lock_duration_sec") / 1000}
                  marks={{
                    60: "60s",
                    300: "5m",
                    600: "10m",
                    1800: "30m",
                    3600: "1h",
                    10800: "3h",
                  }}
                />
              </Form.Item>
            </Col>
            {
              //add buttons to clear token , device ,client or to reregister device
              <Button onClick={async () => {
                try {
                   localStorage.removeItem("device_uuid");
                   localStorage.removeItem("device_token");
                   
                   message.success("Uređaj uspješno deregistrovan.");
                } catch (e:any) {
                  message.error("Neuspjela deregistracija: " + (e?.message || e));
                }
              }}>
                Deregistruj uređaj
              </Button>
            }
          </Row>
        </Space>
      ),
    },
    {
  key: "monry",
  label: "Monry terminal",
  children: (
    <Space direction="vertical" style={{ width: "100%" }}>
      <Form.Item name="monry_enabled" label="Omogući Monry plaćanje" valuePropName="checked">
        <Switch />
      </Form.Item>
      <Row gutter={12}>
        <Col span={12}>
          <Form.Item name="monry_ws_host" label="WebSocket host">
            <Input placeholder="127.0.0.1" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="monry_ws_port" label="WebSocket port">
            <InputNumber min={1} max={65535} style={{ width: "100%" }}   />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={12}>
        <Col span={12}>
          <Form.Item name="monry_terminal_id" label="Terminal ID (opciono)">
            <Input placeholder="npr. 12345678" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="monry_merchant_id" label="Merchant ID (opciono)">
            <Input placeholder="npr. 87654321" />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={12}>
        <Col span={12}>
          <Form.Item name="monry_currency" label="Valuta">
            <Select options={[{ value: "BAM", label: "BAM" }, { value: "EUR", label: "EUR" }]} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="monry_timeout_sec" label="Timeout (sekunde)">
            <InputNumber min={10} max={300} style={{ width: "100%" }}  />
          </Form.Item>
        </Col>
      </Row>

      <Button
        onClick={async () => {
          try {
            const values = await form.getFieldsValue();
            const host = (values.monry_ws_host || "127.0.0.1").trim();
            const port = Number(values.monry_ws_port || 9000);
            const url = `ws://${host}:${port}`;
            const ws = new WebSocket(url);
            await new Promise<void>((resolve, reject) => {
              const to = setTimeout(() => reject(new Error("Timeout")), 3000);
              ws.onopen = () => { clearTimeout(to); ws.close(); resolve(); };
              ws.onerror = () => { clearTimeout(to); reject(new Error("WS error")); };
            });
            message.success("Povezano na Monry WS.");
          } catch (e:any) {
            message.error("Neuspjela konekcija: " + (e?.message || e));
          }
        }}
      >
        Test konekcija
      </Button>

      <Typography.Paragraph type="secondary">
        Port za naše terminale: <b>9000</b>. Najčešće ruta <code>/ecr</code>.
      </Typography.Paragraph>
    </Space>
  ),
},
{
      key: "kiosk_design",
      label: "Kiosk dizajn",
      children: (
        <Space direction="vertical" style={{ width: "100%" }}>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="kiosk_grid_cols" label="Broj kolona (artikli)">
                <InputNumber min={1} max={6} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="kiosk_left_ratio" label="Širina lijevog panela (%)">
                <InputNumber min={15} max={45} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="kiosk_item_img_h" label="Visina slike artikla (px)">
                <InputNumber min={120} max={280} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="kiosk_cat_img_s" label="Veličina slike kategorije (px)">
                <InputNumber min={48} max={120} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={8}>
              <Form.Item name="kiosk_color_bg" label="Pozadina">
                <Input type="color" style={{ width: "100%", height: 40, padding: 2 }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="kiosk_color_text" label="Tekst">
                <Input type="color" style={{ width: "100%", height: 40, padding: 2 }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="kiosk_color_button" label="Dugmad/brand">
                <Input type="color" style={{ width: "100%", height: 40, padding: 2 }} />
              </Form.Item>
            </Col>
          </Row>
          <Typography.Paragraph type="secondary">
            Ove vrijednosti utiču samo na Kiosk ekran (lijevi panel kategorija, mreža artikala, boje i plutajuća korpa).
          </Typography.Paragraph>
        </Space>
      ),
    },

    {
      key: "kitchen",
      label: "Kuhinja",
      children: (
        <Space direction="vertical" style={{ width: "100%" }}>
          <Form.Item
            name="kitchen_max_cols"
            label="Max kolona (1–8)"
            rules={[{ required: true }]}
          >
            <InputNumber min={1} max={8} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            name="kitchen_card_width"
            label="Širina kartice (px, 260–520)"
            rules={[{ required: true }]}
          >
            <InputNumber min={260} max={520} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            name="kitchen_sound_enabled"
            label="Zvuk za nove narudžbe"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
          <Form.Item name="kitchen_sound_kind" label="Vrsta zvuka">
            <Select options={[{ value: "chime", label: "Chime" }]} />
          </Form.Item>
          <Form.Item name="kitchen_sound_volume" label="Jačina zvuka (0–100)">
            <InputNumber min={0} max={100} style={{ width: "100%" }} />
          </Form.Item>
          <Button onClick={testSound}>Test zvuk</Button>
        </Space>
      ),
    },
    {
      key: "labels",
      label: "Etikete",
      children: (
        <Space direction="vertical" style={{ width: "100%" }}>
          <Form.Item name="label_kitchen_title" label="Naslov kuhinje">
            <Input placeholder="Kuhinja — narudžbe za pripremu" />
          </Form.Item>
          <Form.Item name="label_table" label="Oznaka za sto">
            <Input placeholder="Sto" />
          </Form.Item>
          <Form.Item name="label_take" label="Dugme: Preuzmi">
            <Input placeholder="Preuzmi" />
          </Form.Item>
          <Form.Item name="label_ready" label="Dugme: Spremno">
            <Input placeholder="Spremno" />
          </Form.Item>
        </Space>
      ),
    },
  ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={780}
      title="Podešavanja uređaja"
      confirmLoading={loading}
      onOk={onSave}
      okText="Sačuvaj"
    >
      <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
        Podešavanja se čuvaju lokalno i sinhronizuju na server.
      </Typography.Paragraph>
      <Form form={form} layout="vertical" disabled={!canEdit}>
        <Tabs items={items} animated />
      </Form>
    </Modal>
  );
}

function clampInt(v: any, d: number, min: number, max: number) {
  const n = Number(v);
  if (Number.isFinite(n)) return Math.max(min, Math.min(max, Math.round(n)));
  return d;
}
